import EventBus from "./eventBus";

const DEFAULT_WIDTH = 1920;
const DEFAULT_HEIGHT = 1080;
const DEFAULT_FRAME_RATE = 30;

function supportsWorkerRendering() {
  if (typeof window === "undefined") return false;
  return (
    typeof Worker !== "undefined" &&
    typeof OffscreenCanvas !== "undefined" &&
    typeof HTMLCanvasElement !== "undefined" &&
    typeof HTMLCanvasElement.prototype.transferControlToOffscreen === "function"
  );
}

function supportsTrackProcessing() {
  if (typeof window === "undefined") return false;
  return (
    "MediaStreamTrackProcessor" in window &&
    typeof window.MediaStreamTrackProcessor === "function" &&
    "ReadableStream" in window &&
    typeof window.VideoFrame !== "undefined"
  );
}

function createWorkerUrl() {
  return new URL("../workers/mixWorker.js", import.meta.url);
}

export default class MixEngine {
  constructor({ layoutManager, bus = new EventBus(), width, height } = {}) {
    this.bus = bus;
    this.layoutManager = layoutManager;

    this.canvas = document.createElement("canvas");
    this.canvas.width = width ?? DEFAULT_WIDTH;
    this.canvas.height = height ?? DEFAULT_HEIGHT;

    this.frameRate = DEFAULT_FRAME_RATE;
    this.outputStream = null;

    this.videoSources = {
      screen: null,
      cameras: [],
    };
    this.videoFrameCallbacks = new Map();

    this.useWorker = supportsWorkerRendering();
    this.worker = null;
    this.offscreenCanvas = null;
    this.workerReady = false;
    this.pendingWorkerMessages = [];

    this.isRunning = false;

    this.ctx = null; // lazily created for fallback mode
    this.trackProcessors = new Map();
    this.trackProcessingSupported = supportsTrackProcessing();

    this.fallbackState = {
      animationFrameId: null,
      backgroundRenderInterval: null,
      lastRenderTime: 0,
      frameInterval: 1000 / this.frameRate,
      needsRender: false,
      isRendering: false,
      isDocumentHidden: false,
    };

    this.backgroundPumpInterval = null;

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.renderFallback = this.renderFallback.bind(this);
    this.drawFallbackFrame = this.drawFallbackFrame.bind(this);
    this.handleLayoutChange = this.handleLayoutChange.bind(this);

    this.registerLayoutListeners();
  }

  registerLayoutListeners() {
    if (!this.bus?.on) return;
    this.bus.on("layout:camera", this.handleLayoutChange);
    this.bus.on("layout:pip-config", this.handleLayoutChange);
    this.bus.on("layout:changed", this.handleLayoutChange);
  }

  canProcessTracks() {
    return this.useWorker && this.trackProcessingSupported;
  }

  handleLayoutChange() {
    this.sendLayoutState();
  }

  ensureWorkerInitialized() {
    if (!this.useWorker || this.worker) return;

    try {
      const workerUrl = createWorkerUrl();
      this.worker = new Worker(workerUrl, { type: "module" });
      this.workerReady = false;
      this.pendingWorkerMessages = [];

      this.worker.onmessage = (event) => {
        const { type } = event.data || {};
        if (type === "ready") {
          this.workerReady = true;
          this.flushPendingWorkerMessages();
          this.sendLayoutState();
        } else if (type === "disposed") {
          this.worker = null;
          this.workerReady = false;
        }
      };

      this.worker.onerror = (error) => {
        console.error("Mix worker error", error);
        this.fallbackToMainThread();
      };

      this.worker.onmessageerror = (error) => {
        console.error("Mix worker message error", error);
        this.fallbackToMainThread();
      };

      this.offscreenCanvas = this.canvas.transferControlToOffscreen();
      const payload = {
        canvas: this.offscreenCanvas,
        width: this.canvas.width,
        height: this.canvas.height,
        frameRate: this.frameRate,
        pipConfig: this.layoutManager?.getPipConfig?.(),
        activeCameraIndex: this.layoutManager?.getActiveCameraIndex?.() ?? 0,
      };
      this.worker.postMessage({ type: "init", payload }, [
        this.offscreenCanvas,
      ]);
    } catch (error) {
      console.error("Failed to initialize mix worker", error);
      this.fallbackToMainThread();
    }
  }

  fallbackToMainThread() {
    if (this.worker) {
      try {
        this.worker.terminate();
      } catch (terminateError) {
        console.warn("Unable to terminate mix worker", terminateError);
      }
    }
    this.worker = null;
    this.workerReady = false;
    if (this.pendingWorkerMessages.length) {
      this.pendingWorkerMessages.forEach(({ transfer }) => {
        transfer?.forEach?.((item) => {
          try {
            item.close?.();
          } catch (closeError) {
            console.debug(
              "Failed to close transferable during fallback",
              closeError
            );
          }
        });
      });
    }
    this.pendingWorkerMessages = [];
    this.offscreenCanvas = null;
    this.useWorker = false;
    this.stopAllTrackProcessing();

    if (this.videoSources.screen?.stream && !this.videoSources.screen.video) {
      const video = this.createVideoElement(this.videoSources.screen.stream);
      if (video) {
        video.__mixSourceType = "screen";
        this.videoSources.screen.video = video;
        this.queueVideoFrameCallback(video);
      }
    }

    this.videoSources.cameras = this.videoSources.cameras.map(
      ({ stream, video }, index) => {
        if (video) {
          return { stream, video };
        }
        const cameraVideo = this.createVideoElement(stream);
        if (cameraVideo) {
          cameraVideo.__mixSourceType = "camera";
          cameraVideo.__mixCameraIndex = index;
          this.queueVideoFrameCallback(cameraVideo);
        }
        return { stream, video: cameraVideo };
      }
    );

    this.ensureFallbackContext();
  }

  flushPendingWorkerMessages() {
    if (!this.workerReady || !this.worker) return;
    while (this.pendingWorkerMessages.length) {
      const { message, transfer } = this.pendingWorkerMessages.shift();
      try {
        this.worker.postMessage(message, transfer);
      } catch (error) {
        console.warn("Failed to post pending worker message", error);
      }
    }
  }

  postWorkerMessage(type, payload = {}, transfer = []) {
    if (!this.worker) return;
    const message = { type, payload };
    if (!this.workerReady && type !== "init") {
      if (transfer?.length) {
        transfer.forEach((item) => {
          try {
            item.close?.();
          } catch (closeError) {
            console.debug(
              "Unable to release transferable before queue",
              closeError
            );
          }
        });
        return;
      }
      this.pendingWorkerMessages.push({ message, transfer });
      return;
    }
    try {
      this.worker.postMessage(message, transfer);
    } catch (error) {
      console.warn("Failed to post message to worker", error);
      if (transfer?.length) {
        transfer.forEach((item) => {
          try {
            item.close?.();
          } catch (closeError) {
            console.debug("Unable to release transferable", closeError);
          }
        });
      }
    }
  }

  ensureFallbackContext() {
    if (!this.ctx) {
      this.ctx = this.canvas.getContext("2d");
    }
  }

  getTrackKey(sourceType, cameraIndex = 0) {
    return `${sourceType}:${cameraIndex ?? 0}`;
  }

  startTrackProcessing({ stream, sourceType, cameraIndex = 0 }) {
    if (!this.canProcessTracks()) return false;
    const track = stream?.getVideoTracks?.()[0];
    if (!track) return false;

    const key = this.getTrackKey(sourceType, cameraIndex);
    this.stopTrackProcessing(key);

    try {
      const Processor = window.MediaStreamTrackProcessor;
      const processor = new Processor({ track });
      const reader = processor.readable.getReader();
      const controller = { track, processor, reader, active: true };

      const pump = async () => {
        while (controller.active) {
          let result;
          try {
            result = await reader.read();
          } catch (error) {
            if (controller.active) {
              console.warn("Track processor read error", error);
            }
            break;
          }

          if (!controller.active) {
            if (result?.value) {
              try {
                result.value.close();
              } catch (closeError) {
                console.debug("Unable to close frame after stop", closeError);
              }
            }
            break;
          }

          if (!result || result.done) {
            break;
          }

          const frame = result.value;
          if (!frame) {
            continue;
          }

          if (!this.worker || !this.workerReady) {
            frame.close();
            continue;
          }

          try {
            this.postWorkerMessage(
              "frame",
              { sourceType, cameraIndex, frame },
              [frame]
            );
          } catch (error) {
            console.warn("Unable to transfer VideoFrame to worker", error);
            try {
              frame.close();
            } catch (closeError) {
              console.debug(
                "Failed to close frame after transfer error",
                closeError
              );
            }
          }
        }

        try {
          reader.releaseLock();
        } catch (releaseError) {
          console.debug("Unable to release track reader", releaseError);
        }
      };

      controller.loop = pump();
      this.trackProcessors.set(key, controller);
      return true;
    } catch (error) {
      console.warn("Failed to start track processor", error);
      return false;
    }
  }

  stopTrackProcessing(key) {
    const controller = this.trackProcessors.get(key);
    if (!controller) return;
    controller.active = false;
    try {
      controller.reader.cancel().catch(() => {});
    } catch (error) {
      console.debug("Track reader cancel failed", error);
    }
    this.trackProcessors.delete(key);
  }

  stopAllTrackProcessing() {
    [...this.trackProcessors.keys()].forEach((key) =>
      this.stopTrackProcessing(key)
    );
    this.trackProcessors.clear();
  }

  setDimensions({ width, height }) {
    const shouldResize = Boolean(width || height);
    if (width) this.canvas.width = width;
    if (height) this.canvas.height = height;

    if (!shouldResize) return;

    if (this.useWorker) {
      this.postWorkerMessage("resize", {
        width: this.canvas.width,
        height: this.canvas.height,
      });
    } else {
      this.ensureFallbackContext();
      this.fallbackState.needsRender = true;
    }
  }

  createVideoElement(stream) {
    if (!stream) return null;
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    // Keep element with realistic dimensions in layout tree so macOS/Chromium/WebKit
    // compositors do not suspend GPU video decoding when tab is in background.
    video.style.position = "fixed";
    video.style.top = "0";
    video.style.left = "0";
    video.style.width = "320px";
    video.style.height = "180px";
    video.style.opacity = "0.001";
    video.style.pointerEvents = "none";
    video.style.zIndex = "-1000";
    video.setAttribute("aria-hidden", "true");

    const tryPlay = () => {
      if (video.paused && video.readyState >= 2) {
        video.play().catch(() => {
          /* ignore autoplay issues */
        });
      }
    };
    video.onloadedmetadata = tryPlay;
    video.oncanplay = tryPlay;
    video.onloadeddata = tryPlay;

    document.body.appendChild(video);
    tryPlay();
    return video;
  }

  destroyVideoElement(video) {
    if (!video) return;
    this.cancelVideoFrameCallback(video);
    if (this.useWorker) {
      const sourceType = video.__mixSourceType;
      const cameraIndex = video.__mixCameraIndex;
      if (sourceType) {
        this.postWorkerMessage("clear-source", { sourceType, cameraIndex });
      }
    }
    video.pause();
    try {
      video.srcObject = null;
    } catch (error) {
      /* ignore */
    }
    if (video.parentNode) {
      video.parentNode.removeChild(video);
    }
  }

  setScreenStream(stream) {
    if (this.videoSources.screen) {
      this.stopTrackProcessing(this.getTrackKey("screen"));
      this.destroyVideoElement(this.videoSources.screen.video);
    }
    this.postWorkerMessage("clear-source", { sourceType: "screen" });
    if (!stream) {
      this.videoSources.screen = null;
      return;
    }

    const processed = this.startTrackProcessing({
      stream,
      sourceType: "screen",
    });
    let video = null;

    if (!processed) {
      video = this.createVideoElement(stream);
      if (video) {
        video.__mixSourceType = "screen";
      }
      this.queueVideoFrameCallback(video);
    }

    this.videoSources.screen = {
      stream,
      video,
    };
    this.sendLayoutState();
  }

  setCameraStreams(streams = []) {
    this.videoSources.cameras.forEach(({ video }, index) => {
      this.stopTrackProcessing(this.getTrackKey("camera", index));
      this.destroyVideoElement(video);
      this.postWorkerMessage("clear-source", {
        sourceType: "camera",
        cameraIndex: index,
      });
    });

    this.videoSources.cameras = streams.filter(Boolean).map((stream, index) => {
      const processed = this.startTrackProcessing({
        stream,
        sourceType: "camera",
        cameraIndex: index,
      });

      let video = null;

      if (!processed) {
        video = this.createVideoElement(stream);
        if (video) {
          video.__mixSourceType = "camera";
          video.__mixCameraIndex = index;
        }
        this.queueVideoFrameCallback(video);
      }

      return { stream, video };
    });
    this.sendLayoutState();
  }

  getOutputStream({ frameRate = DEFAULT_FRAME_RATE } = {}) {
    if (Number.isFinite(frameRate) && frameRate > 0) {
      this.frameRate = frameRate;
      this.fallbackState.frameInterval = 1000 / this.frameRate;
      if (this.useWorker) {
        this.postWorkerMessage("start", { frameRate: this.frameRate });
      }
    }

    if (!this.outputStream) {
      this.outputStream = this.canvas.captureStream(this.frameRate);
    }

    return this.outputStream;
  }

  start({ frameRate = DEFAULT_FRAME_RATE } = {}) {
    this.isRunning = true;
    if (Number.isFinite(frameRate) && frameRate > 0) {
      this.frameRate = frameRate;
      this.fallbackState.frameInterval = 1000 / this.frameRate;
    }

    if (this.useWorker) {
      this.ensureWorkerInitialized();
      this.postWorkerMessage("start", { frameRate: this.frameRate });
    } else {
      this.ensureFallbackContext();
      this.fallbackState.needsRender = true;
      if (!this.fallbackState.animationFrameId) {
        this.fallbackState.animationFrameId = requestAnimationFrame(
          this.renderFallback
        );
      }
    }

    this.attachVisibilityListener();
    this.sendLayoutState();

    return this.getOutputStream({ frameRate: this.frameRate });
  }

  renderFallback() {
    if (!this.isRunning) {
      this.fallbackState.animationFrameId = null;
      return;
    }

    const now = performance.now();
    if (!this.fallbackState.lastRenderTime) {
      this.fallbackState.lastRenderTime = now;
    }

    const elapsed = now - this.fallbackState.lastRenderTime;
    try {
      if (
        elapsed >= this.fallbackState.frameInterval ||
        this.fallbackState.needsRender
      ) {
        this.drawFallbackFrame(now);
      }
    } catch (error) {
      console.warn("Fallback render frame failed", error);
    } finally {
      if (this.isRunning) {
        this.fallbackState.animationFrameId = requestAnimationFrame(
          this.renderFallback
        );
      } else {
        this.fallbackState.animationFrameId = null;
      }
    }
  }

  drawFallbackFrame(now = performance.now()) {
    if (this.fallbackState.isRendering) return;
    this.fallbackState.isRendering = true;

    try {
      this.fallbackState.lastRenderTime = now;
      const drawRoutine = this.layoutManager?.getDrawRoutine?.();
      if (!drawRoutine) return;

      const screenVideo = this.videoSources.screen?.video;
      const cameraVideos = this.videoSources.cameras.map(({ video }) => video);

      if (!this.ctx) {
        this.ensureFallbackContext();
      }
      if (!this.ctx) return;

      if (screenVideo || cameraVideos.length) {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        try {
          drawRoutine({
            ctx: this.ctx,
            canvas: this.canvas,
            sources: { screen: screenVideo, cameras: cameraVideos },
            activeCameraIndex:
              this.layoutManager?.getActiveCameraIndex?.() ?? 0,
          });
        } catch (error) {
          console.warn("Draw routine failed for this frame", error);
        }
      } else {
        this.ctx.fillStyle = "#0f172a";
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      }
    } finally {
      this.fallbackState.needsRender = false;
      this.fallbackState.isRendering = false;
    }
  }

  queueVideoFrameCallback(video) {
    if (!video || typeof video.requestVideoFrameCallback !== "function") return;
    this.cancelVideoFrameCallback(video);

    const callback = async () => {
      this.queueVideoFrameCallback(video);

      if (!this.isRunning) return;

      if (this.useWorker) {
        if (!this.workerReady || !("createImageBitmap" in window)) {
          return;
        }
        if (video.__mixBitmapPending) {
          return;
        }
        video.__mixBitmapPending = true;
        try {
          const bitmap = await createImageBitmap(video);
          const payload = {
            sourceType: video.__mixSourceType,
            cameraIndex: video.__mixCameraIndex,
            bitmap,
          };
          this.postWorkerMessage("frame", payload, [bitmap]);
        } catch (error) {
          console.warn("Unable to create image bitmap", error);
        } finally {
          video.__mixBitmapPending = false;
        }
      } else {
        this.fallbackState.needsRender = true;
        if (this.fallbackState.isDocumentHidden) {
          this.drawFallbackFrame();
        }
      }
    };

    const handle = video.requestVideoFrameCallback(callback);
    this.videoFrameCallbacks.set(video, { handle, callback });
  }

  cancelVideoFrameCallback(video) {
    if (!video) return;
    const entry = this.videoFrameCallbacks.get(video);
    if (entry && typeof video.cancelVideoFrameCallback === "function") {
      video.cancelVideoFrameCallback(entry.handle);
    }
    this.videoFrameCallbacks.delete(video);
    if (video.__mixBitmapPending) {
      video.__mixBitmapPending = false;
    }
  }

  clearVideoFrameCallbacks() {
    [...this.videoFrameCallbacks.keys()].forEach((video) =>
      this.cancelVideoFrameCallback(video)
    );
    this.videoFrameCallbacks.clear();
  }

  sendLayoutState() {
    if (!this.useWorker || !this.workerReady) return;
    const pipConfig = this.layoutManager?.getPipConfig?.();
    const activeCameraIndex = this.layoutManager?.getActiveCameraIndex?.() ?? 0;
    this.postWorkerMessage("layout", { pipConfig, activeCameraIndex });
  }

  attachVisibilityListener() {
    if (typeof document !== "undefined") {
      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
      this.handleVisibilityChange();
    }
  }

  detachVisibilityListener() {
    if (typeof document !== "undefined") {
      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
    }
  }

  handleVisibilityChange() {
    if (typeof document === "undefined") return;
    this.fallbackState.isDocumentHidden = document.visibilityState === "hidden";

    if (this.fallbackState.isDocumentHidden) {
      if (!this.useWorker) {
        this.startFallbackBackgroundRender();
      }
      this.startBackgroundFramePump();
    } else {
      if (!this.useWorker) {
        this.stopFallbackBackgroundRender();
        this.fallbackState.needsRender = true;
        if (this.isRunning && !this.fallbackState.animationFrameId) {
          this.fallbackState.animationFrameId = requestAnimationFrame(
            this.renderFallback
          );
        }
      }
      this.stopBackgroundFramePump();
    }
  }

  startFallbackBackgroundRender() {
    if (this.useWorker) return;
    if (this.fallbackState.backgroundRenderInterval) return;
    const interval = Math.max(33, Math.floor(this.fallbackState.frameInterval));
    this.fallbackState.backgroundRenderInterval = setInterval(() => {
      if (!this.fallbackState.isDocumentHidden) return;
      this.drawFallbackFrame();
    }, interval);
  }

  stopFallbackBackgroundRender() {
    if (this.useWorker) return;
    if (!this.fallbackState.backgroundRenderInterval) return;
    clearInterval(this.fallbackState.backgroundRenderInterval);
    this.fallbackState.backgroundRenderInterval = null;
  }

  startBackgroundFramePump() {
    if (this.backgroundPumpInterval) return;
    const interval = Math.max(33, Math.floor(this.fallbackState.frameInterval));
    this.backgroundPumpInterval = setInterval(() => {
      if (!this.isRunning || !this.fallbackState.isDocumentHidden) return;

      if (this.useWorker && this.worker && this.workerReady) {
        const videosToPump = [];
        if (this.videoSources.screen?.video) {
          videosToPump.push(this.videoSources.screen.video);
        }
        this.videoSources.cameras.forEach(({ video }) => {
          if (video) videosToPump.push(video);
        });

        videosToPump.forEach(async (video) => {
          if (!video || video.readyState < 2 || video.__mixBitmapPending)
            return;
          video.__mixBitmapPending = true;
          try {
            const bitmap = await createImageBitmap(video);
            const payload = {
              sourceType: video.__mixSourceType,
              cameraIndex: video.__mixCameraIndex,
              bitmap,
            };
            this.postWorkerMessage("frame", payload, [bitmap]);
          } catch (error) {
            // ignore
          } finally {
            video.__mixBitmapPending = false;
          }
        });
      } else if (!this.useWorker) {
        this.drawFallbackFrame();
      }
    }, interval);
  }

  stopBackgroundFramePump() {
    if (this.backgroundPumpInterval) {
      clearInterval(this.backgroundPumpInterval);
      this.backgroundPumpInterval = null;
    }
  }

  stop() {
    this.isRunning = false;

    if (this.useWorker) {
      this.postWorkerMessage("stop");
      this.stopAllTrackProcessing();
    } else {
      if (this.fallbackState.animationFrameId) {
        cancelAnimationFrame(this.fallbackState.animationFrameId);
        this.fallbackState.animationFrameId = null;
      }
      this.stopFallbackBackgroundRender();
    }

    this.stopBackgroundFramePump();
    this.detachVisibilityListener();
    this.clearVideoFrameCallbacks();

    if (this.outputStream) {
      this.outputStream.getTracks().forEach((track) => track.stop());
      this.outputStream = null;
    }

    this.fallbackState.lastRenderTime = 0;
    this.fallbackState.needsRender = false;
    this.fallbackState.isRendering = false;
    this.fallbackState.isDocumentHidden = false;
  }

  dispose() {
    this.stop();
    this.stopAllTrackProcessing();
    if (this.videoSources.screen) {
      this.destroyVideoElement(this.videoSources.screen.video);
    }
    this.videoSources.cameras.forEach(({ video }) =>
      this.destroyVideoElement(video)
    );
    this.videoSources = { screen: null, cameras: [] };
    this.clearVideoFrameCallbacks();
    if (this.useWorker) {
      this.postWorkerMessage("clear-source", {});
    }
    this.bus?.emit?.("mix:disposed");
  }
}
